import { db } from './index';
import { users, adminRequests, notifications } from './schema';
import { eq } from 'drizzle-orm';

export interface CreateUserData {
  clerkId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
}

export interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  isActive?: boolean;
  isAdmin?: boolean;
}

// Crear un nuevo usuario
export async function createUser(userData: CreateUserData) {
  try {
    const [newUser] = await db.insert(users).values({
      clerkId: userData.clerkId,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      imageUrl: userData.imageUrl,
    }).returning();

    return { success: true, data: newUser };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: 'Failed to create user' };
  }
}

// Obtener usuario por Clerk ID
export async function getUserByClerkId(clerkId: string) {
  try {
    const [user] = await db.select().from(users).where(eq(users.clerkId, clerkId));
    return { success: true, data: user || null };
  } catch (error) {
    console.error('Error fetching user by Clerk ID:', error);
    return { success: false, error: 'Failed to fetch user' };
  }
}

// Obtener usuario por email
export async function getUserByEmail(email: string) {
  try {
    const [user] = await db.select().from(users).where(eq(users.email, email));
    return { success: true, data: user || null };
  } catch (error) {
    console.error('Error fetching user by email:', error);
    return { success: false, error: 'Failed to fetch user' };
  }
}

// Actualizar usuario
export async function updateUser(clerkId: string, updateData: UpdateUserData) {
  try {
    const [updatedUser] = await db
      .update(users)
      .set({
        ...updateData,
        updatedAt: new Date(),
      })
      .where(eq(users.clerkId, clerkId))
      .returning();

    return { success: true, data: updatedUser };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error: 'Failed to update user' };
  }
}

// Desactivar usuario (soft delete)
export async function deactivateUser(clerkId: string) {
  try {
    const [deactivatedUser] = await db
      .update(users)
      .set({
        isActive: false,
        updatedAt: new Date(),
      })
      .where(eq(users.clerkId, clerkId))
      .returning();

    return { success: true, data: deactivatedUser };
  } catch (error) {
    console.error('Error deactivating user:', error);
    return { success: false, error: 'Failed to deactivate user' };
  }
}

// Obtener todos los usuarios activos
export async function getActiveUsers() {
  try {
    const activeUsers = await db.select().from(users).where(eq(users.isActive, true));
    return { success: true, data: activeUsers };
  } catch (error) {
    console.error('Error fetching active users:', error);
    return { success: false, error: 'Failed to fetch users' };
  }
}

// Obtener todos los usuarios (para admin)
export async function getAllUsers() {
  try {
    const allUsers = await db.select().from(users).orderBy(users.createdAt);
    return { success: true, data: allUsers };
  } catch (error) {
    console.error('Error fetching all users:', error);
    return { success: false, error: 'Failed to fetch users' };
  }
}

// Crear solicitud de administrador
export async function createAdminRequest(targetClerkId: string, requesterClerkId: string) {
  try {
    // Get the internal ID of the target user
    const [targetUser] = await db.select().from(users).where(eq(users.clerkId, targetClerkId));

    if (!targetUser) {
      return { success: false, error: 'Target user not found' };
    }

    const [newRequest] = await db.insert(adminRequests).values({
      userId: targetUser.id,
      requestedBy: requesterClerkId,
    }).returning();

    return { success: true, data: newRequest };
  } catch (error) {
    console.error('Error creating admin request:', error);
    return { success: false, error: 'Failed to create admin request' };
  }
}

// Aprobar solicitud de administrador
export async function approveAdminRequest(requestId: string) {
  try {
    // 1. Get the request
    const [request] = await db.select().from(adminRequests).where(eq(adminRequests.id, requestId));

    if (!request) {
      return { success: false, error: 'Request not found' };
    }

    if (request.status !== 'PENDING') {
      return { success: false, error: `Request is already ${request.status}` };
    }

    // 2. Update request AND Update user (sequentially without transaction)
    await db.update(adminRequests)
      .set({ status: 'APPROVED', updatedAt: new Date() })
      .where(eq(adminRequests.id, requestId));

    await db.update(users)
      .set({ isAdmin: true, updatedAt: new Date() })
      .where(eq(users.id, request.userId));

    return { success: true, message: 'Approved successfully' };
  } catch (error) {
    console.error('Error approving admin request:', error);
    return { success: false, error: 'Failed to approve' };
  }
}

// ─────────────────────────────────────────────
// Notificaciones
// ─────────────────────────────────────────────

// Crear una nueva notificación para un usuario
export async function createNotification(userId: string, title: string, message: string) {
  try {
    const [notification] = await db.insert(notifications).values({
      userId,
      title,
      message,
    }).returning();
    return { success: true, data: notification };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: 'Failed to create notification' };
  }
}

// Obtener notificaciones de un usuario (sin borradas)
export async function getNotificationsForUser(userId: string) {
  try {
    const result = await db
      .select()
      .from(notifications)
      .where(eq(notifications.userId, userId))
      .orderBy(notifications.createdAt);
    const filtered = result.filter((n) => n.deletedAt === null);
    return { success: true, data: filtered };
  } catch (error) {
    console.error('Error fetching notifications for user:', error);
    return { success: false, error: 'Failed to fetch notifications' };
  }
}

// Obtener TODAS las notificaciones (para admin)
export async function getAllNotifications() {
  try {
    const result = await db
      .select()
      .from(notifications)
      .orderBy(notifications.createdAt);
    return { success: true, data: result };
  } catch (error) {
    console.error('Error fetching all notifications:', error);
    return { success: false, error: 'Failed to fetch notifications' };
  }
}

// Marcar una notificación como leída
export async function markNotificationRead(id: string) {
  try {
    const [updated] = await db
      .update(notifications)
      .set({ isRead: true, updatedAt: new Date() })
      .where(eq(notifications.id, id))
      .returning();
    return { success: true, data: updated };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: 'Failed to update notification' };
  }
}

// Marcar todas las notificaciones de un usuario como leídas
export async function markAllNotificationsRead(userId: string) {
  try {
    await db
      .update(notifications)
      .set({ isRead: true, updatedAt: new Date() })
      .where(eq(notifications.userId, userId));
    return { success: true };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: 'Failed to update notifications' };
  }
}

// Archivar una notificación
export async function archiveNotification(id: string) {
  try {
    const [updated] = await db
      .update(notifications)
      .set({ isArchived: true, updatedAt: new Date() })
      .where(eq(notifications.id, id))
      .returning();
    return { success: true, data: updated };
  } catch (error) {
    console.error('Error archiving notification:', error);
    return { success: false, error: 'Failed to archive notification' };
  }
}

// Soft-delete de una notificación
export async function deleteNotification(id: string) {
  try {
    const [updated] = await db
      .update(notifications)
      .set({ deletedAt: new Date(), updatedAt: new Date() })
      .where(eq(notifications.id, id))
      .returning();
    return { success: true, data: updated };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error: 'Failed to delete notification' };
  }
}

// Hard-delete de una notificación
export async function hardDeleteNotification(id: string) {
  try {
    const [deleted] = await db
      .delete(notifications)
      .where(eq(notifications.id, id))
      .returning();
    return { success: true, data: deleted };
  } catch (error) {
    console.error('Error hard-deleting notification:', error);
    return { success: false, error: 'Failed to hard-delete notification' };
  }
}